// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — cliente API BPMM (prompt 0735, Fase 3)
// Consume /api/v1/process/madurez/*. Espejo de las funciones madurez de
// shared/services/api.ts pero para la escala BPMM (N1-N5) y los 7 pilares.
// ════════════════════════════════════════════════════════════════════════════
import { request, getToken, BASE as API_ROOT } from '../../../shared/services/api';

export type NivelBpmm = 'N1' | 'N2' | 'N3' | 'N4' | 'N5';
export type PilarBpmm =
  | 'ESTRATEGIA'
  | 'PROCESOS'
  | 'METODO'
  | 'TECNOLOGIA'
  | 'PERSONAS'
  | 'GOBIERNO'
  | 'CULTURA';
export type ColorBpmm = 'VERDE' | 'AMARILLO' | 'ROJO';
export type EstadoBpmm = 'BORRADOR' | 'EN_REVISION' | 'APROBADA' | 'OBSOLETA';
export type TipoEjercicioBpmm =
  | 'DIAGNOSTICO_INICIAL'
  | 'EVALUACION_PERIODICA'
  | 'POST_TRANSFORMACION'
  | 'AUDITORIA_EXTERNA';

export interface EvaluacionBpmm {
  id: string;
  codigo: string | null;
  tipoEjercicio: TipoEjercicioBpmm;
  fechaEvaluacion: string;
  nivelGlobal: NivelBpmm | null;
  estado: EstadoBpmm;
  coberturaModelo: string | null;
  nivelGlobalCuestionario: NivelBpmm | null;
  coberturaCuestionario: string | null;
  observaciones: string | null;
}

export interface PilarEvaluacionBpmm {
  id: string;
  pilar: PilarBpmm;
  nivel: NivelBpmm;
  color: ColorBpmm | null;
  subIndicadorValor: string | null;
  observaciones: string | null;
  nivelCuestionario: NivelBpmm | null;
  colorCuestionario: ColorBpmm | null;
  valorCuestionario: string | null;
}

export interface HeatmapBpmm {
  evaluacion: Pick<
    EvaluacionBpmm,
    | 'id'
    | 'tipoEjercicio'
    | 'estado'
    | 'nivelGlobal'
    | 'coberturaModelo'
    | 'nivelGlobalCuestionario'
    | 'coberturaCuestionario'
    | 'fechaEvaluacion'
  >;
  pilares: PilarEvaluacionBpmm[];
}

export interface SiembraBpmm {
  pilares: PilarEvaluacionBpmm[];
  nivelGlobal: NivelBpmm;
  coberturaModelo: number;
}

const BASE = '/process/madurez';

export function fetchEvaluacionesBpmm() {
  return request<{ data: EvaluacionBpmm[] }>(`${BASE}/evaluaciones`);
}
export function crearEvaluacionBpmm(body: { tipoEjercicio?: TipoEjercicioBpmm } = {}) {
  return request<{ data: EvaluacionBpmm }>(`${BASE}/evaluaciones`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
export function sembrarPilaresBpmm(id: string) {
  return request<{ data: SiembraBpmm }>(`${BASE}/evaluaciones/${id}/sembrar`, { method: 'POST' });
}
export function sembrarCuestionarioBpmm(id: string) {
  return request<{ data: unknown }>(`${BASE}/evaluaciones/${id}/sembrar-cuestionario`, {
    method: 'POST',
  });
}
export function fetchHeatmapBpmm(id: string) {
  return request<{ data: HeatmapBpmm }>(`${BASE}/evaluaciones/${id}/heatmap`);
}
export function aprobarEvaluacionBpmm(id: string) {
  return request<{ data: EvaluacionBpmm }>(`${BASE}/evaluaciones/${id}/aprobar`, {
    method: 'POST',
  });
}

// ── Cuestionario BPMM ─────────────────────────────────────────────────────────
export type TipoRespuestaBpmm = 'ESCALA_1_5' | 'TEXTO_LIBRE' | 'BOOLEAN';
export type EjeLiderazgoBpmm = 'CONCIENCIA' | 'ALINEAMIENTO' | 'COMPORTAMIENTO' | 'ESTILO';

export interface PreguntaBpmm {
  id: string;
  pilar: PilarBpmm;
  ejeLiderazgo: EjeLiderazgoBpmm | null;
  enunciado: string;
  orden: number;
  tipoRespuesta: TipoRespuestaBpmm;
}
export interface CuestionarioBpmm {
  id: string;
  codigo: string;
  nombre: string;
  preguntas: PreguntaBpmm[];
}
export interface RespuestaInputBpmm {
  preguntaId: string;
  valor?: number | null;
  valorTexto?: string | null;
  nota?: string | null;
}
export interface RespuestaGuardadaBpmm {
  preguntaId: string;
  valor: number | null;
  valorTexto: string | null;
  nota: string | null;
}

export function fetchCuestionarioBpmm() {
  return request<{ data: CuestionarioBpmm }>(`${BASE}/cuestionario`);
}
export function fetchRespuestasBpmm(id: string) {
  return request<{ data: RespuestaGuardadaBpmm[] }>(`${BASE}/evaluaciones/${id}/respuestas`);
}
export function guardarRespuestasBpmm(id: string, respuestas: RespuestaInputBpmm[]) {
  return request<{ data: { guardadas: number; respondenteId: string } }>(
    `${BASE}/evaluaciones/${id}/respuestas`,
    { method: 'POST', body: JSON.stringify({ respuestas }) },
  );
}

// ── Análisis IA BPMM ──────────────────────────────────────────────────────────
export type FuenteAnalisisBpmm = 'CUESTIONARIO' | 'REAL' | 'AMBAS';
export type EstadoAnalisisBpmm = 'BORRADOR' | 'GENERADO' | 'VALIDADO';

export interface RecomendacionBpmm {
  pilar: PilarBpmm;
  nivelGatillo: NivelBpmm | null;
  textoFinal: string;
  justificacion: string;
  prioridad: number;
}
export interface DafoBpmm {
  fortalezas: string[];
  debilidades: string[];
  oportunidades: string[];
  amenazas: string[];
}
export interface AnalisisBpmm {
  id: string;
  diagnosticoNarrativo: string;
  recomendaciones: RecomendacionBpmm[];
  fortalezas: string[];
  riesgos: string[];
  conclusionEjecutiva: string | null;
  matrizDafo: DafoBpmm | null;
  estado: EstadoAnalisisBpmm;
  fuente: FuenteAnalisisBpmm;
  modeloUsado: string | null;
  proveedor: string | null;
  creadoEn: string;
}

export function generarAnalisisBpmm(id: string, fuente?: FuenteAnalisisBpmm) {
  return request<{ data: AnalisisBpmm }>(`${BASE}/evaluaciones/${id}/analisis`, {
    method: 'POST',
    body: JSON.stringify(fuente ? { fuente } : {}),
  });
}
export function fetchAnalisisVigenteBpmm(id: string) {
  return request<{ data: AnalisisBpmm | null }>(`${BASE}/evaluaciones/${id}/analisis`);
}
export function validarAnalisisBpmm(analisisId: string) {
  return request<{ data: AnalisisBpmm }>(`${BASE}/analisis/${analisisId}/validar`, {
    method: 'POST',
  });
}

// ── Conclusión del ingeniero de gobernanza (observaciones de la evaluación) ────
export function guardarObservacionesBpmm(id: string, observaciones: string) {
  return request<{ data: EvaluacionBpmm }>(`${BASE}/evaluaciones/${id}/observaciones`, {
    method: 'PATCH',
    body: JSON.stringify({ observaciones }),
  });
}

// ── Discrepancias del cuestionario (multi-respondedor) ────────────────────────
export interface DiscrepanciaBpmm {
  preguntaId: string;
  pilar: PilarBpmm;
  enunciado: string;
  nRespondentes: number;
  media: number;
  varianza: number;
  discrepancia: boolean;
  valores: number[];
}
export function fetchDiscrepanciasBpmm(id: string) {
  return request<{ data: DiscrepanciaBpmm[] }>(`${BASE}/evaluaciones/${id}/discrepancias`);
}

// ── Exportación / informe (descargas binarias, prompt 0736 Parte B) ───────────
// request() fuerza Content-Type JSON; las descargas usan fetch manual con Bearer.
async function descargarBlobProceso(path: string, fallbackName: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_ROOT}${path}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Error ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fallbackName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
/** Descarga el informe Word (.docx) corporativo de un análisis VALIDADO. */
export function descargarInformeProcesoWord(analisisId: string): Promise<void> {
  return descargarBlobProceso(
    `${BASE}/analisis/${analisisId}/informe`,
    'informe-madurez-proceso.docx',
  );
}
/** Descarga el exportador .py (genera el .docx desde claude.ai). */
export function descargarExportadorPyProceso(analisisId: string): Promise<void> {
  return descargarBlobProceso(
    `${BASE}/analisis/${analisisId}/exportador-py`,
    'generar_informe_madurez_proceso.py',
  );
}
/** Descarga el megaprompt .md (snapshot embebido) para claude.ai. */
export function descargarMegapromptProceso(evaluacionId: string): Promise<void> {
  return descargarBlobProceso(
    `${BASE}/evaluaciones/${evaluacionId}/megaprompt`,
    'megaprompt-madurez-proceso.md',
  );
}
/** Obtiene el informe HTML autónomo y devuelve un object URL blob (el llamador lo revoca). */
export async function obtenerInformeHtmlProceso(evaluacionId: string): Promise<string> {
  const token = getToken();
  const res = await fetch(`${API_ROOT}${BASE}/evaluaciones/${evaluacionId}/informe-html`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Error ${res.status}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
